import { useState } from 'react'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import styles from './styles.module.css'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { IDeliveryCandidate } from '../../../../../app/entities/DeliveryCandidate'
import PrimaryCheckbox from '../../../global/components/checkbox/Checkbox'
import useFetch from '../../../global/hooks/useFetchHook'
import { getRoutes } from '../api'

interface IProps {
  isOpen: boolean
  deliveryCandidate: IDeliveryCandidate | null
  handleClose: () => void
  handleConfirm: (routeObjectId: string, routeInstructionTime: string) => Promise<void>
}

export default function ForceDeliveryCandidateAppointmentModal(props: IProps) {
  const { isOpen, deliveryCandidate, handleClose, handleConfirm } = props
  const [isChecked, setIsChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [routeObjectId, setRouteObjectId] = useState('')
  const [routeInstructionTime, setRouteInstructionTime] = useState('')

  const { data: routes } = useFetch(
    getRoutes(deliveryCandidate?.location?.objectId),
  )

  async function handleForce() {
    if (!routeObjectId || !routeInstructionTime) return
    try {
      setIsLoading(true)
      await handleConfirm(routeObjectId, routeInstructionTime)
      resetState()
    } catch (error) {
      console.error('Failed to force delivery candidate appointment:', error)
    } finally {
      setIsLoading(false)
    }
  }

  function resetState() {
    setIsChecked(false)
    setRouteObjectId('')
    setRouteInstructionTime('')
  }

  function handleCancel() {
    resetState()
    handleClose()
  }

  const isFormValid = isChecked && routeObjectId && routeInstructionTime

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
    >
      <Box className={styles.scheduleModal}>
        <div className={`${styles.scheduleModalPageTitle} font--bold`}>
          Force Appointment for Rejected Delivery
        </div>
        <div style={{ marginBottom: '10px', fontSize: '16px' }}>
          This will create an order from this rejected delivery candidate and
          force it onto the selected route and time.
        </div>
        {deliveryCandidate && (
          <div style={{ marginBottom: '16px', textAlign: 'center' }}>
            <div>
              <strong>TR Order Number:</strong>{' '}
              {deliveryCandidate.tireRackDeliveryData.tireRackOrderNumber}
            </div>
            <div>
              <strong>Customer:</strong>{' '}
              {deliveryCandidate.tireRackDeliveryData.customerFullName}
            </div>
            <div>
              <strong>Delivery Address:</strong>{' '}
              {deliveryCandidate.tireRackDeliveryData.installationPointAddress},{' '}
              {deliveryCandidate.tireRackDeliveryData.installationPointCity},{' '}
              {deliveryCandidate.tireRackDeliveryData.installationPointState}{' '}
              {deliveryCandidate.tireRackDeliveryData.installationPointZipCode}
            </div>
          </div>
        )}
        <div style={{ marginBottom: '16px' }}>
          <TextField
            fullWidth
            label='Route'
            select
            value={routeObjectId}
            onChange={(e) => setRouteObjectId(e.target.value)}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 500,
                    width: 200,
                  },
                },
              },
            }}
          >
            {routes && Array.isArray(routes)
              ? routes.map((route: any, index: number) => {
                  const technicianName = route.technician
                    ? route.technician.name
                    : 'No technician'
                  return (
                    <MenuItem
                      key={index}
                      value={route.objectId}
                    >
                      {`${route.dayFormatted} - ${technicianName}`}
                    </MenuItem>
                  )
                })
              : []}
          </TextField>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <TextField
            fullWidth
            type='time'
            label='Appointment Time'
            InputLabelProps={{ shrink: true }}
            value={routeInstructionTime}
            onChange={(e) => setRouteInstructionTime(e.target.value)}
          />
        </div>
        <div className={styles.checkBoxContainer}>
          <PrimaryCheckbox
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
          />
          <div>
            Yes, I want to force this delivery to this route
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={handleCancel}
          />
          <PrimaryButton
            buttonName='Force'
            disabled={!isFormValid || isLoading}
            onClick={handleForce}
          />
        </div>
      </Box>
    </Modal>
  )
}
